import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { COMPANY } from "@/data/company";

const navItems = [
  { to: "/", label: "Anasayfa", testId: "nav-home" },
  { to: "/cozumler", label: "Çözümler", testId: "nav-solutions" },
  { to: "/sektorler", label: "Sektörler", testId: "nav-sectors" },
  { to: "/risk-hesaplayici", label: "Risk Skoru", testId: "nav-calculator" },
  { to: "/blog", label: "Blog", testId: "nav-blog" },
  { to: "/iletisim", label: "İletişim", testId: "nav-contact" },
];

function Navbar() {
  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#06090F]/75 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
          <div className="relative h-10 md:h-11 flex items-center justify-center px-1">
            <img
              src="/assets/zake-logo.png"
              alt="ZAKE Mühendislik"
              className="h-full w-auto object-contain transition-all group-hover:scale-105"
              style={{
                imageRendering: "auto",
                filter: "drop-shadow(0 0 8px rgba(255,255,255,0.15))",
              }}
            />
          </div>
          <div className="hidden sm:flex flex-col leading-none border-l border-white/15 pl-3 py-1">
            <span className="text-[15px] font-bold tracking-[0.22em] text-white">
              ZAKE
            </span>
            <span className="font-mono text-[9px] uppercase text-thermal-orange tracking-[0.28em] mt-1">
              Termal · Güvenlik
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `nav-link font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 whitespace-nowrap ${
                  isActive ? "active" : ""
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <span className="hidden lg:flex font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-thermal-green mr-2 self-center animate-pulse" />
            System: Online
          </span>
          <Link
            to="/iletisim"
            data-testid="cta-contact"
            className="hidden sm:inline-flex items-center gap-1.5 bg-thermal-orange hover:bg-thermal-red text-white px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
          >
            Demo Talep <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function MobileTabBar() {
  const location = useLocation();
  const mobileItems = [
    { to: "/", label: "Ana", testId: "nav-home" },
    { to: "/cozumler", label: "Çözüm", testId: "nav-solutions" },
    { to: "/sektorler", label: "Sektör", testId: "nav-sectors" },
    { to: "/risk-hesaplayici", label: "Risk", testId: "nav-calculator" },
    { to: "/iletisim", label: "İletişim", testId: "nav-contact" },
  ];
  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#06090F]/95 backdrop-blur-xl border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5">
        {mobileItems.map((item) => {
          const active =
            item.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-${item.testId}`}
              className={`py-3 text-center font-mono text-[10px] uppercase tracking-[0.1em] min-h-[48px] flex items-center justify-center ${
                active ? "text-thermal-orange" : "text-slate-400"
              }`}
            >
              {item.label}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#06090F] mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-5">
              <img
                src="/assets/zake-logo.png"
                alt="ZAKE"
                className="h-12 w-auto"
                style={{ filter: "drop-shadow(0 0 8px rgba(255,255,255,0.15))" }}
              />
              <div className="border-l border-white/15 pl-4">
                <div className="text-base font-bold tracking-[0.22em] text-white">
                  ZAKE
                </div>
                <div className="font-mono text-[9px] uppercase text-thermal-orange tracking-[0.28em] mt-1">
                  Termal · Güvenlik
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-sm max-w-md leading-relaxed mb-3">
              Görünenin ötesini algılayan yapay zeka destekli termal analiz ve önleyici güvenlik çözümleri.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 max-w-md leading-relaxed">
              {COMPANY.fullName}
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">
              [ Site ]
            </div>
            <ul className="space-y-2 text-sm">
              {navItems.map((i) => (
                <li key={i.to}>
                  <Link
                    to={i.to}
                    className="text-slate-300 hover:text-thermal-orange transition-colors"
                  >
                    {i.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 border-t border-white/10 mt-3">
                <a
                  href="/assets/zake-termal-brosur.pdf"
                  download
                  data-testid="footer-brosur-download"
                  className="inline-flex items-center gap-2 text-thermal-orange hover:text-thermal-yellow transition-colors font-mono text-xs uppercase tracking-[0.18em]"
                >
                  ↓ Broşürü İndir (PDF)
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">
              [ İletişim ]
            </div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-thermal-orange transition-colors"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="font-mono">
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="hover:text-thermal-orange transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="text-slate-400 text-xs leading-relaxed">
                {COMPANY.address}
                <br />
                {COMPANY.district}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            © {new Date().getFullYear()} ZAKE Mühendislik Otomasyon — All Rights Reserved
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            v1.0.0 / TR
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-[#06090F] text-white">
      <Navbar />
      <main className="pt-16 md:pt-20 pb-20 md:pb-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
      <MobileTabBar />
    </div>
  );
}
